// src/index.ts
var ImageSize = {
  ExtraExtraSmall: "XXS",
  ExtraSmall: "XS",
  Small: "S",
  Medium: "M",
  Large: "L",
  ExtraLarge: "XL",
  ExtraExtraLarge: "XXL",
  ExtraExtraExtraLarge: "XXXL",
  ExtraExtraExtraExtraLarge: "XXXXL"
};
var defaultImageSizeMap = {
  [ImageSize.ExtraExtraSmall]: "14",
  [ImageSize.ExtraSmall]: "14",
  [ImageSize.Small]: "14",
  [ImageSize.Medium]: "82",
  [ImageSize.Large]: "83",
  [ImageSize.ExtraLarge]: "84",
  [ImageSize.ExtraExtraLarge]: "85",
  [ImageSize.ExtraExtraExtraLarge]: "86",
  [ImageSize.ExtraExtraExtraExtraLarge]: "87"
};
var defaultImageSizeKeyMap = {
  [ImageSize.ExtraExtraSmall]: "extraExtraSmall",
  [ImageSize.ExtraSmall]: "extraSmall",
  [ImageSize.Small]: "small",
  [ImageSize.Medium]: "medium",
  [ImageSize.Large]: "large",
  [ImageSize.ExtraLarge]: "extraLarge",
  [ImageSize.ExtraExtraLarge]: "extraExtraLarge",
  [ImageSize.ExtraExtraExtraLarge]: "extraExtraExtraLarge",
  [ImageSize.ExtraExtraExtraExtraLarge]: "extraExtraExtraExtraLarge"
};
var reverseMap = {
  extraextrasmall: ImageSize.ExtraExtraSmall,
  xxsmall: ImageSize.ExtraExtraSmall,
  xxs: ImageSize.ExtraExtraSmall,
  extrasmall: ImageSize.ExtraSmall,
  xsmall: ImageSize.ExtraSmall,
  xs: ImageSize.ExtraSmall,
  small: ImageSize.Small,
  s: ImageSize.Small,
  medium: ImageSize.Medium,
  m: ImageSize.Medium,
  md: ImageSize.Medium,
  large: ImageSize.Large,
  l: ImageSize.Large,
  extralarge: ImageSize.ExtraLarge,
  xlarge: ImageSize.ExtraLarge,
  xl: ImageSize.ExtraLarge,
  extraextralarge: ImageSize.ExtraExtraLarge,
  xxlarge: ImageSize.ExtraExtraLarge,
  xxl: ImageSize.ExtraExtraLarge,
  extraextraextralarge: ImageSize.ExtraExtraExtraLarge,
  xxxlarge: ImageSize.ExtraExtraExtraLarge,
  xxxl: ImageSize.ExtraExtraExtraLarge,
  extraextraextraextralarge: ImageSize.ExtraExtraExtraExtraLarge,
  xxxxlarge: ImageSize.ExtraExtraExtraExtraLarge,
  xxxxl: ImageSize.ExtraExtraExtraExtraLarge
};
var protocolRegex = /^[A-Za-z]+:\/\//;
var mpYamsUrlRegex = /(ecg_mp_eps\$_)(#|\d+)(\.jpg)?$/;
function maybeSplitMpYamsUrl(url) {
  const match = url.match(mpYamsUrlRegex);
  if (!match) {
    return null;
  }
  const prefix = url.substring(0, match.index) + match[1];
  const size = match[2];
  const suffix = match[3] || "";
  const isPlaceholder = size === "#";
  return { prefix, size, suffix, isPlaceholder };
}
function maybeSplitIcasPlaceholderUrl(url) {
  const idx = url.indexOf("#");
  return idx >= 0 ? {
    prefix: url.substring(0, idx),
    size: "#",
    suffix: url.substring(idx + 1),
    isPlaceholder: true
  } : null;
}
function maybeSplitEpsLegacyUrl(url) {
  const match = url.match(/\$_(#|\d+)(\.[A-Za-z]+)$/);
  if (!match) {
    return null;
  }
  const size = match[1];
  const isPlaceholder = size === "#";
  const prefix = `${url.substring(0, match.index)}$_`;
  const suffix = match[2];
  return { prefix, size, suffix, isPlaceholder };
}
function isEmptyObject(obj) {
  if (!obj) {
    return true;
  }
  for (const _ in obj) {
    return false;
  }
  return true;
}
function isValidImageSize(s) {
  return Object.values(ImageSize).includes(s);
}
function matchTemplateUrl(url, imageSizeMap = defaultImageSizeMap) {
  const match = maybeSplitMpYamsUrl(url) ?? maybeSplitEpsLegacyUrl(url) ?? maybeSplitIcasPlaceholderUrl(url);
  if (!match) {
    return null;
  }
  const { size, suffix, prefix, isPlaceholder } = match;
  if (!(typeof size === "string" && (isPlaceholder || Object.values(imageSizeMap).map((v) => v.toString()).includes(size)))) {
    return null;
  }
  return { prefix, size, suffix, isPlaceholder };
}
function parseImageSize(size, fallback) {
  if (!isValidImageSize(fallback)) {
    throw new Error("no valid fallback defined");
  }
  if (typeof size !== "string" || !size) {
    return fallback;
  }
  return reverseMap[size.toLowerCase()] || fallback;
}
function withHttps(url) {
  if (typeof url !== "string") {
    return "";
  }
  if (url === "") {
    return "";
  }
  if (url.toLowerCase().startsWith("http://") || url.toLowerCase().startsWith("https://")) {
    return url;
  }
  return url?.startsWith("//") ? `https:${url}` : `https://${url}`;
}
function forceHttps(url) {
  if (typeof url !== "string") {
    return "";
  }
  if (url === "") {
    return "";
  }
  if (url.toLowerCase().startsWith("https://")) {
    return url;
  }
  return url.startsWith("//") ? `https:${url}` : `https://${url.replace(protocolRegex, "")}`;
}
function withHttpsWhenPRURL(url) {
  if (typeof url !== "string") {
    return "";
  }
  return url.startsWith("//") ? `https:${url}` : url;
}
function explodeToImageSizes(templateUrl, sizes, options) {
  if (typeof templateUrl !== "string" || !templateUrl || sizes.length === 0) {
    return {};
  }
  if (options && !isEmptyObject(options) && !(options.hasOwnProperty("keyMap") || options.hasOwnProperty("sizeMap"))) {
    throw new Error("if you use options, you have to supply either keyMap or sizeMap");
  }
  const match = matchTemplateUrl(templateUrl);
  if (!match) {
    return {};
  }
  const prefix = withHttpsWhenPRURL(match.prefix);
  const { suffix } = match;
  const { keyMap = defaultImageSizeKeyMap, sizeMap } = options || {};
  const safeSizeMap = sizeMap ? Object.fromEntries(Object.entries(sizeMap).map(([k, v]) => [k, v.toString()])) : defaultImageSizeMap;
  return Object.fromEntries(
    sizes.filter(isValidImageSize).filter((s) => safeSizeMap.hasOwnProperty(s) && keyMap.hasOwnProperty(s)).map((s) => [keyMap[s], `${prefix}${safeSizeMap[s]}${suffix}`])
  );
}
function getURlWithoutJPG(url) {
  return url.replace(/\.jpg$/i, "");
}
function changeToImageSize(url, targetSize, options) {
  if (typeof url !== "string" || !url) {
    return "";
  }
  const urlWithoutExtension = getURlWithoutJPG(url);
  if (!isValidImageSize(targetSize)) {
    return urlWithoutExtension;
  }
  const { allowPlaceholder, allowedSizes, customSizeMap } = { allowPlaceholder: false, ...options };
  const imageSizeMap = customSizeMap || defaultImageSizeMap;
  const match = matchTemplateUrl(url, imageSizeMap);
  if (!match) {
    return urlWithoutExtension;
  }
  const replacementUrl = getURlWithoutJPG(`${match.prefix}${imageSizeMap[targetSize]}${match.suffix}`);
  if (match.isPlaceholder) {
    return allowPlaceholder ? replacementUrl : urlWithoutExtension;
  }
  if (!allowedSizes) {
    return replacementUrl;
  }
  const allowedMatches = allowedSizes?.filter((s) => isValidImageSize(s) && imageSizeMap[s])?.map((s) => imageSizeMap[s].toString());
  if (allowedMatches && allowedMatches.length > 0 && allowedMatches.includes(match.size)) {
    return replacementUrl;
  }
  return urlWithoutExtension;
}
var changeToImageSizeAndRemoveExtension = changeToImageSize;
function formatImageUrl(url, targetSize, customSizeMap) {
  return changeToImageSize(url, targetSize, {
    allowPlaceholder: true,
    allowedSizes: [],
    customSizeMap
  });
}
function switchImageSize(url, from, to, customSizeMap) {
  return changeToImageSize(url, to, { allowedSizes: [from], customSizeMap });
}
export {
  ImageSize,
  changeToImageSize,
  changeToImageSizeAndRemoveExtension,
  defaultImageSizeKeyMap,
  defaultImageSizeMap,
  explodeToImageSizes,
  forceHttps,
  formatImageUrl,
  getURlWithoutJPG,
  isEmptyObject,
  parseImageSize,
  switchImageSize,
  withHttps,
  withHttpsWhenPRURL
};
//# sourceMappingURL=index.mjs.map